import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  COMPLIANCE_CATEGORY,
  COMPLIANCE_STATUS,
  metaFor,
  type ComplianceItem,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<ComplianceItem>[] = [
  {
    key: "requirement",
    header: "Requirement",
    primary: true,
    render: (row) => (
      <div className="min-w-0 max-w-md">
        <p className="text-sm font-semibold text-foreground-950">{row.requirement}</p>
        <p className="text-xs text-foreground-500 mt-0.5">
          {row.authority || "—"} · {row.applies_to || "—"}
        </p>
      </div>
    ),
  },
  { key: "category", header: "Category", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.category}</span> },
  { key: "owner", header: "Owner", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.owner || "—"}</span> },
  { key: "due", header: "Due", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.due_date)}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(COMPLIANCE_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<ComplianceItem> = {
  table: "compliance_items",
  orderBy: "due_date",
  eyebrow: "Safety & Compliance",
  title: "Compliance",
  subtitle: "The obligations the business must meet — licences, filings, audits and testing — with owners and due dates.",
  searchPlaceholder: "Search requirements, authorities…",
  searchKeys: ["requirement", "category", "authority", "owner", "applies_to"],
  filters: [
    { key: "status", label: "Status", options: optionsFrom(COMPLIANCE_STATUS) },
    { key: "category", label: "Category", options: options(COMPLIANCE_CATEGORY) },
  ],
  statusMap: COMPLIANCE_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Requirements", value: rows.length, icon: "ri-checkbox-multiple-line", tone: "primary" },
    { label: "Compliant", value: rows.filter((row) => row.status === "compliant").length, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "In progress", value: rows.filter((row) => row.status === "in_progress").length, icon: "ri-loader-4-line", tone: "secondary" },
    { label: "Pending / overdue", value: rows.filter((row) => row.status === "pending" || row.status === "overdue").length, icon: "ri-error-warning-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "requirement", label: "Requirement" },
    { key: "category", label: "Category", type: "select", options: options(COMPLIANCE_CATEGORY) },
    { key: "authority", label: "Authority" },
    { key: "applies_to", label: "Applies to" },
    { key: "owner", label: "Owner" },
    { key: "status", label: "Status", type: "select", options: optionsFrom(COMPLIANCE_STATUS) },
    { key: "due_date", label: "Due on", type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  createFields: [
    { key: "requirement", label: "Requirement" },
    { key: "category", label: "Category", type: "select", options: options(COMPLIANCE_CATEGORY) },
    { key: "authority", label: "Authority" },
    { key: "applies_to", label: "Applies to" },
    { key: "owner", label: "Owner" },
    { key: "due_date", label: "Due on", type: "date" },
  ],
  newDefaults: {
    requirement: "",
    category: "Licensing",
    authority: "",
    applies_to: "Rythugaru Agri Tourism",
    status: "pending",
    due_date: null,
    owner: "",
    notes: "",
  },
  emptyTitle: "No compliance items yet",
  emptyMessage: "Track every licence, filing and audit obligation here with an owner and a due date.",
  rowTitle: (row) => row.requirement,
};

export default function AdminCompliance() {
  return <ResourceModule config={config} />;
}